import { useState, useRef, useEffect, MouseEvent, CSSProperties } from 'react'
// 定义通用样式类型
type HandleStyleType = {
	base: CSSProperties
	hover?: CSSProperties
	active?: CSSProperties
	handle?: CSSProperties
}

// 定义具体区域的样式类型
type RightHandleStyle = HandleStyleType & {
	handle: CSSProperties
}

type TopHandleStyle = HandleStyleType & {
	handle: CSSProperties
}
type BottomHandleStyle = HandleStyleType & {
	handle: CSSProperties
}

type CornerHandleStyle = HandleStyleType

// 整体样式对象类型
type DragHandleStyles = {
	top: TopHandleStyle
	right: RightHandleStyle
	bottom: BottomHandleStyle
	corner: CornerHandleStyle
}

// 新增拖拽条样式常量
const DRAG_HANDLE_STYLES = {
	top: {
		base: {
			width: '100%',
			height: '20px',
			top: '-10px',
			background: `linear-gradient(
			    to bottom,
			    rgba(255,255,255, 0.8) 0%,
			    rgba(255,255,255, 0.1) 100%
			  )`,
			transition: 'all 0.2s ease'
		},
		hover: {
			background: `linear-gradient(
			    to bottom,
			    rgba(255,255,255, 0.8) 0%,
			    rgba(255,255,255, 0.1) 100%
			  )`
		},
		handle: {
			width: '10%',
			height: '4px',
			background: 'rgba(0,0,0,0.2)',
			borderRadius: '4px',
			marginTop: '2px'
		}
	},
	right: {
		base: {
			width: '12px',
			height: '100%',
			right: '-6px',
			background: `linear-gradient(
        to left,
        rgba(79, 70, 229, 0.2) 0%,
        rgba(79, 70, 229, 0) 100%
      )`,
			transition: 'all 0.2s ease'
		},
		hover: {
			background: `linear-gradient(
        to left,
        rgba(79, 70, 229, 0.4) 0%,
        rgba(79, 70, 229, 0.1) 100%
      )`
		},
		handle: {
			width: '2px',
			height: '40%',
			background: '#4f46e5',
			borderRadius: '2px',
			boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
		}
	},

	bottom: {
		base: {
			width: '100%',
			height: '12px',
			bottom: '-6px',
			background: `linear-gradient(
        to top,
        rgba(79, 70, 229, 0.2) 0%,
        rgba(79, 70, 229, 0) 100%
      )`,
			transition: 'all 0.2s ease'
		},
		hover: {
			background: `linear-gradient(
        to top,
        rgba(79, 70, 229, 0.4) 0%,
        rgba(79, 70, 229, 0.1) 100%
      )`
		},
		handle: {
			width: '40%',
			height: '2px',
			background: '#4f46e5',
			borderRadius: '2px',
			boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
		}
	},
	corner: {
		base: {
			width: '24px',
			height: '24px',
			background: `conic-gradient(
        from -45deg,
        #4f46e5 90deg,
        transparent 90deg 270deg,
        #4f46e5 270deg
      )`,
			clipPath: 'polygon(100% 0, 0% 100%, 100% 100%)',
			opacity: 0.6,
			transition: 'all 0.2s ease'
		},
		hover: {
			opacity: 1,
			transform: 'scale(1.1)'
		},
		active: {
			opacity: 0.8,
			transform: 'scale(0.95)'
		}
	}
} as DragHandleStyles

const ResizableBox = ({
	initialWidth = 400,
	initialHeight = 200,
	minWidth = 100,
	minHeight = 100,
	children
}: any): JSX.Element => {
	const [size, setSize] = useState({
		width: initialWidth,
		height: initialHeight
	})

	const [hoverState, setHoverState] = useState({
		right: false,
		bottom: false,
		corner: false,
		top: false
	})
	// 新增悬停处理
	const handleHover = (area: string) => (e: { type: string }) => {
		if (e.type === 'mouseenter') {
			setHoverState(prev => ({ ...prev, [area]: true }))
		} else {
			setHoverState(prev => ({ ...prev, [area]: false }))
		}
	}

	// 动态生成拖拽条样式
	const getHandleStyle = (area: keyof DragHandleStyles) => {
		const base = DRAG_HANDLE_STYLES[area].base
		const hover = hoverState[area] ? DRAG_HANDLE_STYLES[area].hover : {}
		return { ...base, ...hover }
	}

	const boxRef = useRef(null)
	const isDragging = useRef(false)
	const dragHandle = useRef('top')
	const startPos = useRef({ x: 0, y: 0 })
	const startSize = useRef({ width: 0, height: 0 })

	// 处理拖拽开始
	const handleMouseDown = (handleType: string, e: MouseEvent<HTMLDivElement, MouseEvent>) => {
		e.preventDefault()
		isDragging.current = true
		dragHandle.current = handleType
		startPos.current = { x: e.clientX, y: e.clientY }
		startSize.current = { ...size }

		document.addEventListener('mousemove', handleMouseMove)
		document.addEventListener('mouseup', handleMouseUp)
		document.body.style.userSelect = 'none' // 防止选中文本
	}

	// 处理拖拽移动
	const handleMouseMove = (e: { clientX: number; clientY: number }) => {
		if (!isDragging.current) return

		const deltaX = e.clientX - startPos.current.x
		const deltaY = e.clientY - startPos.current.y

		const newSize = { ...size }

		switch (dragHandle.current) {
			case 'right':
				newSize.width = Math.max(minWidth, startSize.current.width + deltaX)
				break
			case 'top':
				newSize.height = Math.max(minHeight, startSize.current.height - deltaY)
				break
			case 'bottom':
				newSize.height = Math.max(minHeight, startSize.current.height + deltaY)
				break
			case 'corner':
				newSize.width = Math.max(minWidth, startSize.current.width + deltaX)
				newSize.height = Math.max(minHeight, startSize.current.height + deltaY)
				break
			default:
				break
		}

		setSize(newSize)
	}

	// 处理拖拽结束
	const handleMouseUp = () => {
		isDragging.current = false
		document.removeEventListener('mousemove', handleMouseMove)
		document.removeEventListener('mouseup', handleMouseUp)
		document.body.style.userSelect = ''
	}

	// 拖拽手柄样式
	const handleStyle = (position: string): CSSProperties => ({
		position: 'absolute',
		backgroundColor: 'transparent',
		...getHandleStyle(position),
		...(position === 'top' && {
			top: '-4px',
			left: '0',
			width: '100%',
			height: '20px',
			cursor: 'row-resize'
		}),
		...(position === 'right' && {
			right: '-4px',
			top: '0',
			width: '8px',
			height: '100%',
			cursor: 'col-resize'
		}),
		...(position === 'bottom' && {
			bottom: '-4px',
			left: '0',
			width: '100%',
			height: '8px',
			cursor: 'row-resize'
		}),
		...(position === 'corner' && {
			right: '-8px',
			bottom: '-8px',
			width: '16px',
			height: '16px',
			cursor: 'nwse-resize'
		})
	})

	return (
		<div
			ref={boxRef}
			style={{
				position: 'relative',
				width: size.width + 'px',
				height: size.height + 'px',
				// border: '2px solid #3498db',
				paddingTop: '16px',
				borderRadius: '6px',
				boxSizing: 'border-box',
				overflow: 'hidden',
				transition: 'width 0.2s, height 0.2s'
			}}
		>
			{/* 顶部 */}
			<div
				style={handleStyle('top')}
				onMouseDown={e => handleMouseDown('top', e)}
				onMouseEnter={handleHover('top')}
				onMouseLeave={handleHover('top')}
				className="flex items-center justify-center"
			>
				{<div style={DRAG_HANDLE_STYLES.top.handle}></div>}
			</div>

			{/* 内容区域 */}
			<div style={{ overflow: 'auto', height: '100%' }}>
				{children || <div style={{ padding: '8px' }}>inner content</div>}
			</div>
		</div>
	)
}

export default ResizableBox
